import { useState } from 'react';
import { Copy, Check, ChevronDown, ArrowLeft } from 'lucide-react';

export const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const MONO = 'ui-monospace, "JetBrains Mono", SFMono-Regular, Menlo, monospace';

/* Copy-to-clipboard button with a transient "Copied!" tooltip. */
export function CopyButton({ value, ariaLabel = 'Copy to clipboard' }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={ariaLabel}
      className="inline-flex items-center justify-center"
      style={{
        width: 30, height: 30, borderRadius: 5, flexShrink: 0,
        background: 'transparent', border: '1px solid var(--b2)',
        color: copied ? 'var(--ok)' : 'var(--ts)', cursor: 'pointer',
      }}
      title={copied ? 'Copied!' : 'Copy'}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
}

/* A labelled monospace value row with an optional copy button. */
export function CodeRow({ label, value, copyable = true }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        padding: '0.55rem 0.75rem', borderBottom: '1px solid var(--b1)',
      }}
    >
      <span style={{ color: 'var(--ts)', fontSize: '0.78rem', minWidth: 92, flexShrink: 0 }}>
        {label}
      </span>
      <code
        style={{
          fontFamily: MONO, fontSize: '0.82rem', color: 'var(--white)',
          flex: 1, wordBreak: 'break-all',
        }}
      >
        {value}
      </code>
      {copyable && <CopyButton value={value} ariaLabel={`Copy ${label}`} />}
    </div>
  );
}

/* Status pill shown at the top of each step screen. */
const PILL = {
  waiting:   { dot: 'var(--ts)',     text: 'var(--ts)',    label: 'Waiting for your change' },
  checking:  { dot: 'var(--warn)',   text: 'var(--warn)',  label: 'Checking now…', pulse: true },
  not_found: { dot: 'var(--ts)',     text: 'var(--ts)',    label: 'Not found yet' },
  mismatch:  { dot: 'var(--warn)',   text: 'var(--warn)',  label: "Almost there — value doesn't match" },
  error:     { dot: 'var(--danger)', text: 'var(--danger)',label: 'Could not check' },
  verified:  { dot: 'var(--ok)',     text: 'var(--ok)',    label: '✓ Verified' },
};

export function StatusPill({ state, labelOverride }) {
  const cfg = PILL[state] || PILL.waiting;
  return (
    <span
      className="inline-flex items-center gap-2 font-body"
      style={{
        fontSize: '0.8rem', fontWeight: 500, color: cfg.text,
        padding: '0.3rem 0.7rem', borderRadius: 999,
        border: `1px solid ${cfg.dot}`, background: 'rgba(255,255,255,0.02)',
      }}
    >
      {state !== 'verified' && (
        <span
          style={{
            width: 8, height: 8, borderRadius: 999, background: cfg.dot,
            display: 'inline-block',
            animation: cfg.pulse ? 'acodia-pulse 1s ease-in-out infinite' : 'none',
          }}
        />
      )}
      {labelOverride || cfg.label}
      <style>{'@keyframes acodia-pulse{0%,100%{opacity:1}50%{opacity:0.3}}'}</style>
    </span>
  );
}

/* Collapsible disclosure section. */
export function Collapsible({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid var(--b1)', borderRadius: 8, marginTop: '0.75rem' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="font-body inline-flex items-center justify-between w-full"
        style={{
          padding: '0.6rem 0.85rem', background: 'transparent', border: 'none',
          color: 'var(--white)', fontSize: '0.84rem', cursor: 'pointer', fontWeight: 500,
        }}
      >
        <span>{title}</span>
        <ChevronDown size={16} style={{
          transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s',
          color: 'var(--ts)',
        }} />
      </button>
      {open && (
        <div style={{ padding: '0 0.85rem 0.85rem', color: 'var(--tm)', fontSize: '0.82rem',
                      lineHeight: 1.6 }}>
          {children}
        </div>
      )}
    </div>
  );
}

/* "Choose another method" back link shown on each step screen. */
export function MethodSwitcher({ onBack }) {
  return (
    <button
      type="button"
      onClick={onBack}
      className="font-body inline-flex items-center gap-1.5"
      style={{ background: 'transparent', border: 'none', color: 'var(--ts)',
               fontSize: '0.82rem', cursor: 'pointer', padding: 0 }}
    >
      <ArrowLeft size={14} /> Choose another method
    </button>
  );
}

/* Shared scaffold for the three step screens: header (switcher + status pill +
   Check now), mismatch diff, the step content, and the footer auto-poll note. */
export function VerifyStepShell({
  title, pillState, lastCheck, checking, onCheckNow, onSwitchMethod, autoNote, children,
}) {
  const mismatch = lastCheck && lastCheck.status === 'mismatch';
  return (
    <div>
      <MethodSwitcher onBack={onSwitchMethod} />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: '1rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
        <h1 className="font-display" style={{ fontSize: '1.6rem', fontWeight: 700,
              color: 'var(--white)' }}>
          {title}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <StatusPill state={pillState} />
          <button
            type="button"
            onClick={onCheckNow}
            disabled={checking}
            data-testid="verify-check-now"
            className="font-body font-medium inline-flex items-center gap-1.5"
            style={{
              padding: '0.4rem 0.85rem', borderRadius: 6, border: '1px solid var(--b2)',
              background: 'transparent', color: 'var(--white)',
              cursor: checking ? 'wait' : 'pointer', fontSize: '0.82rem',
            }}
          >
            ↻ {checking ? 'Checking…' : 'Check now'}
          </button>
        </div>
      </div>

      {mismatch && (
        <div className="font-body" style={{ marginTop: '0.85rem', padding: '0.7rem 0.9rem',
              borderRadius: 8, background: 'rgba(217,119,6,0.1)',
              border: '1px solid rgba(217,119,6,0.3)', color: 'var(--warn)', fontSize: '0.82rem' }}>
          Almost there — the value doesn&apos;t match.<br />
          <span style={{ color: 'var(--ts)' }}>Found:</span> {String(lastCheck.found_value)}<br />
          <span style={{ color: 'var(--ts)' }}>Expected:</span> {String(lastCheck.expected_value)}
        </div>
      )}
      {lastCheck && lastCheck.status === 'error' && lastCheck.error_message && (
        <div className="font-body" style={{ marginTop: '0.85rem', padding: '0.7rem 0.9rem',
              borderRadius: 8, background: 'rgba(220,38,38,0.08)',
              border: '1px solid rgba(220,38,38,0.25)', color: 'var(--danger)', fontSize: '0.82rem' }}>
          {lastCheck.error_message}
        </div>
      )}

      {children}

      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <button
          type="button"
          onClick={onCheckNow}
          disabled={checking}
          className="font-body font-medium inline-flex items-center gap-1.5"
          style={{ padding: '0.6rem 1.3rem', borderRadius: 6, border: 'none',
                   background: 'var(--a)', color: '#fff', cursor: checking ? 'wait' : 'pointer' }}
        >
          ↻ {checking ? 'Checking…' : 'Check now'}
        </button>
        <p className="font-body" style={{ color: 'var(--ts)', fontSize: '0.78rem', marginTop: '0.6rem' }}>
          {autoNote}
        </p>
      </div>
    </div>
  );
}

/* Numbered step block heading. */
export function StepBlock({ n, title, children }) {
  return (
    <div style={{ marginTop: '1.5rem' }}>
      <h3 className="font-display" style={{ color: 'var(--white)', fontSize: '1rem',
            fontWeight: 600, marginBottom: '0.5rem' }}>
        <span style={{ color: 'var(--a)' }}>{n}.</span> {title}
      </h3>
      {children}
    </div>
  );
}
